import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listClients,
  createClient,
  updateClient,
  deactivateClient,
  addClientCredit,
  type ClientListParams,
  type Client,
  type ClientInput,
} from "../api/clients";
import { getMyPermissions } from "../api/users";

const PAGE_SIZE = 50;

function fmtCredit(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function AddCreditModal({
  client,
  onClose,
  onSaved,
}: {
  client: Client;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Manual credit top-up (admin)");
  const num = Number(amount);
  const valid = !Number.isNaN(num) && num > 0;

  const save = useMutation({
    mutationFn: () => addClientCredit(client.usr_id, { amount: num, description }),
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-3">
        <h2 className="text-lg font-bold">Add credit</h2>
        <p className="text-sm text-gray-600">
          Account <span className="font-medium">{client.username}</span> — current balance{" "}
          <span className="font-mono">{fmtCredit(client.credit)}</span>.
        </p>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Amount (USD)</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Note (audit description)</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {valid && (
          <p className="text-xs text-gray-500">
            New balance will be <span className="font-mono">{fmtCredit(client.credit + num)}</span>.
          </p>
        )}
        <p className="text-xs text-amber-600">
          This is a real credit adjustment recorded against the account. It is not a card charge.
        </p>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!valid || save.isPending}
            onClick={() => {
              if (confirm(`Add ${fmtCredit(num)} credit to ${client.username}?`)) save.mutate();
            }}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Adding…" : "Add credit"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientEditor({
  client,
  isAdmin,
  onClose,
  onSaved,
}: {
  client: Client | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [username, setUsername] = useState(client?.username ?? "");
  const [email, setEmail] = useState(client?.email ?? "");
  const [company, setCompany] = useState(client?.company ?? "");
  const [phone, setPhone] = useState(client?.phone ?? "");
  const [channel, setChannel] = useState(client?.channel ?? 0);
  const [billingMode, setBillingMode] = useState(client?.billing_mode ?? "flat");
  const [cardPaymentEnabled, setCardPaymentEnabled] = useState(
    Boolean(client?.card_payment_enabled),
  );
  const [cardMaxAmount, setCardMaxAmount] = useState<number>(
    client?.card_payment_max_amount ?? 1000,
  );

  const save = useMutation({
    mutationFn: () => {
      const fields: ClientInput = {
        email: email || null,
        company: company || null,
        phone: phone || null,
        channel,
        billing_mode: billingMode,
      };
      if (isAdmin) {
        fields.card_payment_enabled = cardPaymentEnabled;
        if (cardPaymentEnabled && Number.isFinite(cardMaxAmount) && cardMaxAmount > 0) {
          fields.card_payment_max_amount = cardMaxAmount;
        }
      }
      return client
        ? updateClient(client.usr_id, fields)
        : createClient({ username, ...fields }).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">{client ? "Edit" : "New"} client account</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Username</label>
          <input
            disabled={!!client}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Company</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Email</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Phone</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Concurrent channels</label>
          <input
            type="number"
            min={0}
            className="w-32 border border-gray-300 rounded px-3 py-2 text-sm"
            value={channel}
            onChange={(e) => setChannel(Number(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Billing mode</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={billingMode}
            onChange={(e) => setBillingMode(e.target.value)}
          >
            <option value="flat">Flat — per-plan $/min</option>
            <option value="npa_nxx">NPA-NXX rate sheet (60/6)</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">
            NPA-NXX uses the uploaded rate sheet with interstate/intrastate rates chosen per call.
          </p>
        </div>
        {isAdmin && (
          <div className="p-3 rounded border border-gray-200 bg-gray-50 space-y-2.5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={cardPaymentEnabled}
                onChange={(e) => setCardPaymentEnabled(e.target.checked)}
              />
              <span className="text-xs text-gray-600">
                <span className="font-medium block text-sm text-gray-900">
                  Allow credit card payments (Authorize.Net)
                </span>
                Opt-in per account. When enabled, this customer can pay by credit card on Billing.
              </span>
            </label>
            {cardPaymentEnabled && (
              <div className="pl-6">
                <label className="block text-xs text-gray-500 mb-1">
                  Max amount per transaction (USD)
                </label>
                <input
                  type="number"
                  min={10}
                  step={1}
                  value={cardMaxAmount}
                  onChange={(e) => setCardMaxAmount(Number(e.target.value))}
                  className="w-40 border border-gray-300 rounded px-2.5 py-1.5 text-sm font-mono bg-white"
                />
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-gray-400">Credit balances are managed separately under Billing.</p>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={(!client && !username) || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function ClientsPage() {
  const [params, setParams] = useState<ClientListParams>({
    page: 1,
    per_page: PAGE_SIZE,
    active_only: false,
  });

  const qc = useQueryClient();
  const [editor, setEditor] = useState<Client | "new" | null>(null);
  const [creditFor, setCreditFor] = useState<Client | null>(null);
  const { data: me } = useQuery({ queryKey: ["me-permissions"], queryFn: getMyPermissions });
  const isAdmin = me?.is_admin ?? true;

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["clients", params],
    queryFn: () => listClients(params),
    placeholderData: keepPreviousData,
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => deactivateClient(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });

  const page = params.page ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Clients (Billing Accounts)</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={params.active_only ?? false}
              onChange={(e) =>
                setParams((p) => ({ ...p, active_only: e.target.checked, page: 1 }))
              }
            />
            Active only
          </label>
          <button
            onClick={() => setEditor("new")}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            New client
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} accounts`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Username</th>
                <th className="px-4 py-2">Company</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2 text-right">Credit</th>
                <th className="px-4 py-2 text-right">Channels</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-gray-500">
                    No clients.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((c) => (
                  <tr key={c.usr_id}>
                    <td className="px-4 py-2 font-medium">{c.username}</td>
                    <td className="px-4 py-2 text-gray-600">{c.company || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{c.email || "—"}</td>
                    <td className="px-4 py-2 text-right">{fmtCredit(c.credit)}</td>
                    <td className="px-4 py-2 text-right">{c.channel}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          c.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {c.active ? "active" : "inactive"}
                      </span>
                      {c.card_payment_enabled && (
                        <span className="ml-1.5 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          card pay
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button onClick={() => setCreditFor(c)} className="text-brand hover:underline">
                        Add credit
                      </button>
                      <button onClick={() => setEditor(c)} className="text-brand hover:underline">
                        Edit
                      </button>
                      {c.active && (
                        <button
                          onClick={() => {
                            if (confirm(`Deactivate client "${c.username}"?`)) deactivate.mutate(c.usr_id);
                          }}
                          className="text-red-600 hover:underline"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editor && (
        <ClientEditor
          client={editor === "new" ? null : editor}
          isAdmin={isAdmin}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["clients"] });
            qc.invalidateQueries({ queryKey: ["card-payment-config"] });
            qc.invalidateQueries({ queryKey: ["me-permissions"] });
          }}
        />
      )}

      {creditFor && (
        <AddCreditModal
          client={creditFor}
          onClose={() => setCreditFor(null)}
          onSaved={() => {
            setCreditFor(null);
            qc.invalidateQueries({ queryKey: ["clients"] });
            qc.invalidateQueries({ queryKey: ["balances"] });
          }}
        />
      )}
    </div>
  );
}
